/**
 * Responsible-gambling limits — the rules the screen must get RIGHT, kept
 * out of the markup so they can be pinned by tests.
 *
 * The contract (migration 0043, `/me/responsible-gambling-limits`) encodes
 * one asymmetry and the screen must never soften it: a NEW or STRICTER limit
 * takes effect immediately; an increase, a removal or a period change is held
 * for the operator-configured delay. Cool-off and self-exclusion are
 * non-reversible inside their window — there is no shorten or revoke path on
 * the API and so there is none here. The offered periods, the increase delay
 * and the minimum self-exclusion are read from `/me` public params, never
 * hard-coded: Boss's standing ruling is that these are settings, not
 * invariants, because this is simulated betting.
 */
import type { MeResponse, ResponsibleGamblingLimits } from '@/types/api';

export type LimitKind = 'deposit' | 'loss';

/** The subset of `/me` params this feature reads. */
export type LimitParams = Pick<
  MeResponse['params'],
  | 'responsibleGamblingIncreaseDelayHours'
  | 'responsibleGamblingMinSelfExclusionDays'
  | 'responsibleGamblingLimitPeriodDays'
>;

/** The cool-off durations the contract enumerates. Not a param — a fixed enum. */
export const COOL_OFF_DAYS = [1, 7, 30] as const;
export type CoolOffDays = (typeof COOL_OFF_DAYS)[number];

export type SelfExclusionChoice = number | 'permanent';

/**
 * Self-exclusion options: the operator minimum first, then the longer fixed
 * terms that still clear it, then permanent. A minimum above every fixed term
 * leaves [min, permanent] — never an empty list, never an option below min.
 */
export function selfExclusionOptions(minDays: number): SelfExclusionChoice[] {
  const fixed = [90, 180, 365];
  const days = [minDays, ...fixed.filter((d) => d > minDays)];
  return [...days, 'permanent'];
}

/** One limit as the customer should read it: what binds now, what is queued. */
export interface LimitView {
  kind: LimitKind;
  activeCents: number | null;
  activePeriodDays: number | null;
  pendingCents: number | null;
  pendingPeriodDays: number | null;
  /** ISO time the pending change becomes active; null when nothing is pending. */
  effectiveAt: string | null;
  /** True when a change is queued — including a queued REMOVAL (pending null). */
  hasPending: boolean;
}

export function limitView(limits: ResponsibleGamblingLimits, kind: LimitKind): LimitView {
  const effectiveAt =
    kind === 'deposit' ? limits.depositChangeEffectiveAt : limits.lossChangeEffectiveAt;
  return {
    kind,
    activeCents: kind === 'deposit' ? limits.depositLimitCents : limits.lossLimitCents,
    activePeriodDays: kind === 'deposit' ? limits.depositPeriodDays : limits.lossPeriodDays,
    pendingCents:
      kind === 'deposit' ? limits.pendingDepositLimitCents : limits.pendingLossLimitCents,
    pendingPeriodDays:
      kind === 'deposit' ? limits.pendingDepositPeriodDays : limits.pendingLossPeriodDays,
    effectiveAt,
    // A pending removal carries pendingCents === null with a non-null
    // effectiveAt, so the timestamp is the authority, not the amount.
    hasPending: effectiveAt !== null,
  };
}

export type ChangeTiming = 'immediate' | 'delayed' | 'none';

/**
 * Will this proposed change bind now or be held? Mirrors the server rule so
 * the confirm line tells the truth BEFORE the customer commits:
 *   - no active limit → setting one is immediate
 *   - same period, lower amount → immediate (stricter)
 *   - anything else (higher amount, removal, period change) → delayed
 *   - identical to active → nothing to do
 */
export function classifyChange(
  active: { cents: number | null; periodDays: number | null },
  proposed: { cents: number | null; periodDays: number | null },
): ChangeTiming {
  if (active.cents === proposed.cents && active.periodDays === proposed.periodDays) return 'none';
  if (active.cents === null) {
    // Setting a fresh limit is immediate; "removing" an absent one is nothing.
    return proposed.cents === null ? 'none' : 'immediate';
  }
  if (proposed.cents === null) return 'delayed';
  if (proposed.periodDays !== active.periodDays) return 'delayed';
  return proposed.cents < active.cents ? 'immediate' : 'delayed';
}

/**
 * Validate the form before anything is sent. Returns a message or null.
 * Amount is whole dollars ≥ 1; the period must be one the operator offers.
 */
export function validateLimitInput(
  dollars: string,
  periodDays: number | null,
  offeredPeriods: number[],
): string | null {
  const trimmed = dollars.trim();
  if (trimmed === '') return 'Enter an amount.';
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return 'Enter a dollar amount like 200 or 49.50.';
  const cents = Math.round(Number(trimmed) * 100);
  if (!Number.isSafeInteger(cents) || cents < 100) return 'The limit must be at least $1.';
  if (periodDays === null || !offeredPeriods.includes(periodDays)) {
    return 'Choose one of the offered periods.';
  }
  return null;
}

export function dollarsToCents(dollars: string): number {
  return Math.round(Number(dollars.trim()) * 100);
}

/** The PUT body for one limit, carrying the version for optimistic concurrency. */
export function buildLimitPayload(
  kind: LimitKind,
  version: number,
  cents: number | null,
  periodDays: number | null,
): {
  expectedVersion: number;
  depositLimitCents?: number | null;
  depositPeriodDays?: number | null;
  lossLimitCents?: number | null;
  lossPeriodDays?: number | null;
} {
  return kind === 'deposit'
    ? { expectedVersion: version, depositLimitCents: cents, depositPeriodDays: periodDays }
    : { expectedVersion: version, lossLimitCents: cents, lossPeriodDays: periodDays };
}

/** "1 day" / "7 days" / "4 weeks" — periods are read from params, so this must
 *  cope with any integer, not just the defaults. */
export function describePeriod(days: number): string {
  if (days === 1) return '1 day';
  if (days % 7 === 0 && days > 7) return `${days / 7} weeks`;
  return `${days} days`;
}

export function describeDelay(hours: number): string {
  if (hours % 24 === 0 && hours >= 24) {
    const d = hours / 24;
    return d === 1 ? '24 hours' : `${d} days`;
  }
  return `${hours} hours`;
}

/** Restriction state as the customer should read it. */
export interface RestrictionView {
  code: ResponsibleGamblingLimits['restrictionCode'];
  heading: string;
  detail: string;
  /** Whether placing bets is blocked right now. */
  blocksBetting: boolean;
}

export function restrictionView(
  limits: ResponsibleGamblingLimits,
  now: Date = new Date(),
): RestrictionView | null {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

  if (limits.restrictionCode === 'responsible_gambling_self_excluded') {
    const permanent = limits.selfExcludedUntil === null;
    return {
      code: limits.restrictionCode,
      heading: permanent ? 'You are permanently self-excluded' : 'You are self-excluded',
      detail: permanent
        ? 'Betting and deposits are closed on this account. This cannot be reversed from the app.'
        : `Betting and deposits are closed until ${fmt(limits.selfExcludedUntil as string)}. This cannot be shortened.`,
      blocksBetting: true,
    };
  }
  if (limits.restrictionCode === 'responsible_gambling_cool_off') {
    const until = limits.coolOffUntil;
    const stillActive = until !== null && new Date(until).getTime() > now.getTime();
    return {
      code: limits.restrictionCode,
      heading: 'You are taking a break',
      detail: stillActive
        ? `Betting is paused until ${fmt(until as string)}. This cannot be ended early.`
        : 'Your cooling-off period is ending.',
      blocksBetting: true,
    };
  }
  return null;
}
